import React, { useState, useEffect } from 'react';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CalculatorIcon,
  BuildingStorefrontIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { nutritionApi, DailyFeedMix } from '../api/nutrition';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { useFormatting } from '../hooks/useFormatting';

interface CostAnalysis {
  totalCost: number;
  averageCostPerKg: number;
  averageCostPerBird: number;
  trendDirection: 'up' | 'down' | 'stable';
  trendPercentage: number;
  costByCategory: {
    pellets: number;
    seeds: number;
    vegetables: number;
    supplements: number;
    other: number;
  };
  topExpenseComponents: Array<{
    name: string;
    cost: number;
    percentage: number;
    quantity: number;
  }>;
  monthlyCosts: Array<{
    month: string;
    cost: number;
    quantity: number;
  }>;
  projectedCost: number;
}

interface FeedCostAnalysisProps {
  selectedLocation?: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

// Helper function to safely convert to number
const safeNumber = (value: any): number => {
  return typeof value === 'number' ? value : parseFloat(value) || 0;
};

export default function FeedCostAnalysis({
  selectedLocation,
  dateRange
}: FeedCostAnalysisProps) {
  const { formatCurrency, formatDate } = useFormatting();
  const [costAnalysis, setCostAnalysis] = useState<CostAnalysis | null>(null);
  const [dailyMixes, setDailyMixes] = useState<DailyFeedMix[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<'7days' | '30days' | '90days' | 'custom'>('30days');
  const [showProjections, setShowProjections] = useState(true);

  useEffect(() => {
    loadCostData();
  }, [selectedLocation, selectedPeriod, dateRange]);

  const loadCostData = async () => {
    try {
      setIsLoading(true);

      let startDate = dateRange.startDate;
      let endDate = dateRange.endDate;

      // Override with period selection if not custom
      if (selectedPeriod !== 'custom') {
        const end = new Date();
        const days = selectedPeriod === '7days' ? 7 : selectedPeriod === '30days' ? 30 : 90;
        startDate = subDays(end, days).toISOString().split('T')[0];
        endDate = end.toISOString().split('T')[0];
      }

      const mixes = await nutritionApi.getDailyFeedMixes({
        startDate,
        endDate,
        location: selectedLocation
      });

      setDailyMixes(mixes);
      const analysis = await calculateCostAnalysis(mixes);
      setCostAnalysis(analysis);

    } catch (error) {
      console.error('Failed to load cost data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateCostAnalysis = async (mixes: DailyFeedMix[]): Promise<CostAnalysis> => {
    if (mixes.length === 0) {
      return {
        totalCost: 0,
        averageCostPerKg: 0,
        averageCostPerBird: 0,
        trendDirection: 'stable',
        trendPercentage: 0,
        costByCategory: { pellets: 0, seeds: 0, vegetables: 0, supplements: 0, other: 0 },
        topExpenseComponents: [],
        monthlyCosts: [],
        projectedCost: 0
      };
    }

    const totalCost = mixes.reduce((sum, mix) => sum + (mix.totalCost || 0), 0);
    const totalQuantity = mixes.reduce((sum, mix) => sum + mix.totalQuantity, 0);
    const averageCostPerKg = totalQuantity > 0 ? totalCost / totalQuantity : 0;
    
    // Estimate bird count (this would come from actual bird data in real implementation)
    const estimatedBirdCount = 50; // Mock value
    const averageCostPerBird = totalCost / (mixes.length * estimatedBirdCount);

    // Calculate trend
    const firstHalf = mixes.slice(0, Math.floor(mixes.length / 2));
    const secondHalf = mixes.slice(Math.floor(mixes.length / 2));
    
    const firstHalfAvg = firstHalf.reduce((sum, mix) => sum + (mix.totalCost || 0), 0) / firstHalf.length;
    const secondHalfAvg = secondHalf.reduce((sum, mix) => sum + (mix.totalCost || 0), 0) / secondHalf.length;
    
    const trendPercentage = firstHalfAvg > 0 ? ((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100 : 0;
    const trendDirection = Math.abs(trendPercentage) < 5 ? 'stable' : 
                          trendPercentage > 0 ? 'up' : 'down';

    // Aggregate components by category
    const componentCosts = new Map<string, { cost: number; quantity: number; category: string }>();
    
    mixes.forEach(mix => {
      mix.feedComponents.forEach(component => {
        const existing = componentCosts.get(component.feedStockName) || 
          { cost: 0, quantity: 0, category: categorizeComponent(component.feedStockName) };
        
        // Estimate component cost (would come from actual pricing data)
        const estimatedUnitCost = getEstimatedUnitCost(component.feedStockName);
        const componentCost = component.quantity * estimatedUnitCost;
        
        existing.cost += componentCost;
        existing.quantity += component.quantity;
        componentCosts.set(component.feedStockName, existing);
      });
    });

    // Calculate cost by category
    const costByCategory = {
      pellets: 0,
      seeds: 0,
      vegetables: 0,
      supplements: 0,
      other: 0
    };

    componentCosts.forEach(({ cost, category }) => {
      if (category in costByCategory) {
        (costByCategory as any)[category] += cost;
      } else {
        costByCategory.other += cost;
      }
    });

    // Top expense components
    const topExpenseComponents = Array.from(componentCosts.entries())
      .map(([name, data]) => ({
        name,
        cost: data.cost,
        percentage: totalCost > 0 ? (data.cost / totalCost) * 100 : 0,
        quantity: data.quantity
      }))
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 10);

    // Monthly costs
    const monthlyData = new Map<string, { cost: number; quantity: number }>();
    mixes.forEach(mix => {
      const month = format(new Date(mix.feedDate), 'yyyy-MM');
      const existing = monthlyData.get(month) || { cost: 0, quantity: 0 };
      existing.cost += mix.totalCost || 0;
      existing.quantity += mix.totalQuantity;
      monthlyData.set(month, existing);
    });

    const monthlyCosts = Array.from(monthlyData.entries())
      .map(([month, data]) => ({
        month: format(new Date(month + '-01'), 'MMM yyyy'),
        cost: data.cost,
        quantity: data.quantity
      }))
      .sort((a, b) => a.month.localeCompare(b.month));

    // Project next month's cost based on trend
    const lastMonthCost = monthlyCosts.length > 0 ? monthlyCosts[monthlyCosts.length - 1].cost : 0;
    const projectedCost = lastMonthCost * (1 + (trendPercentage / 100));

    return {
      totalCost,
      averageCostPerKg,
      averageCostPerBird,
      trendDirection,
      trendPercentage,
      costByCategory,
      topExpenseComponents,
      monthlyCosts,
      projectedCost
    };
  };

  const categorizeComponent = (name: string): string => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('pellet')) return 'pellets';
    if (lowerName.includes('seed')) return 'seeds';
    if (lowerName.includes('vegetable') || lowerName.includes('fruit')) return 'vegetables';
    if (lowerName.includes('vitamin') || lowerName.includes('mineral') || lowerName.includes('supplement')) return 'supplements';
    return 'other';
  };

  const getEstimatedUnitCost = (componentName: string): number => {
    // Mock pricing - in real implementation, this would come from feed stock data
    const prices: { [key: string]: number } = {
      'pellets': 2.50,
      'seeds': 3.00,
      'vegetables': 1.50,
      'supplements': 8.00,
      'other': 2.00
    };
    
    const category = categorizeComponent(componentName);
    return prices[category] || prices.other;
  };

  const getTrendIcon = () => {
    if (!costAnalysis) return null;
    
    switch (costAnalysis.trendDirection) {
      case 'up':
        return <ArrowTrendingUpIcon className="h-5 w-5 text-red-500" />;
      case 'down':
        return <ArrowTrendingDownIcon className="h-5 w-5 text-green-500" />;
      default:
        return <div className="h-5 w-5 bg-gray-400 rounded-full"></div>;
    }
  };

  const costTrendChartData = {
    labels: costAnalysis?.monthlyCosts.map(m => m.month) || [],
    datasets: [
      {
        label: 'Monthly Cost',
        data: costAnalysis?.monthlyCosts.map(m => typeof m.cost === 'number' ? m.cost : parseFloat(m.cost) || 0) || [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.1,
      },
      ...(showProjections && costAnalysis ? [{
        label: 'Projected Cost',
        data: [...costAnalysis.monthlyCosts.map(m => typeof m.cost === 'number' ? m.cost : parseFloat(m.cost) || 0), typeof costAnalysis.projectedCost === 'number' ? costAnalysis.projectedCost : parseFloat(costAnalysis.projectedCost) || 0],
        borderColor: 'rgb(156, 163, 175)',
        backgroundColor: 'rgba(156, 163, 175, 0.1)',
        borderDash: [5, 5],
        fill: false,
      }] : [])
    ],
  };

  const categoryDistributionData = {
    labels: ['Pellets', 'Seeds', 'Vegetables', 'Supplements', 'Other'],
    datasets: [
      {
        data: costAnalysis ? [
          costAnalysis.costByCategory.pellets,
          costAnalysis.costByCategory.seeds,
          costAnalysis.costByCategory.vegetables,
          costAnalysis.costByCategory.supplements,
          costAnalysis.costByCategory.other
        ] : [],
        backgroundColor: [
          '#3B82F6',
          '#10B981',
          '#F59E0B',
          '#EF4444',
          '#6B7280'
        ],
      },
    ],
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
        <span className="ml-2 text-sm text-slate-600">Loading cost analysis...</span>
      </div>
    );
  }

  if (!costAnalysis) {
    return (
      <div className="text-center py-12">
        <CurrencyDollarIcon className="mx-auto h-12 w-12 text-slate-400" />
        <h3 className="mt-2 text-sm font-medium text-slate-900">No cost data available</h3>
        <p className="mt-1 text-sm text-slate-500">No feed mixes found for the selected period.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-slate-900 flex items-center">
            <CurrencyDollarIcon className="h-6 w-6 text-cyan-600 mr-2" />
            Feed Cost Analysis
          </h2>
          <div className="flex items-center space-x-3">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value as any)}
              className="text-sm border-gray-300 rounded-md focus:ring-cyan-500 focus:border-cyan-500"
            >
              <option key="7days-1753461297310" value="7days">Last 7 Days</option>
              <option key="30days-1753461297310" value="30days">Last 30 Days</option>
              <option key="90days-1753461297310" value="90days">Last 90 Days</option>
              <option key="custom-1753461297310" value="custom">Custom Range</option>
            </select>
            <label className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={showProjections}
                onChange={(e) => setShowProjections(e.target.checked)}
                className="mr-2 h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              Show Projections
            </label>
          </div>
        </div>
      </div>

      {/* Cost Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CurrencyDollarIcon className="h-8 w-8 text-cyan-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-slate-500">Total Cost</p>
              <p className="text-2xl font-semibold text-slate-900">
                {formatCurrency(typeof costAnalysis.totalCost === 'number' ? costAnalysis.totalCost : parseFloat(costAnalysis.totalCost) || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ScaleIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-slate-500">Cost per kg</p>
              <p className="text-2xl font-semibold text-slate-900">
                {formatCurrency(typeof costAnalysis.averageCostPerKg === 'number' ? costAnalysis.averageCostPerKg : parseFloat(costAnalysis.averageCostPerKg) || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BuildingStorefrontIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-slate-500">Cost per Bird</p>
              <p className="text-2xl font-semibold text-slate-900">
                {formatCurrency(typeof costAnalysis.averageCostPerBird === 'number' ? costAnalysis.averageCostPerBird : parseFloat(costAnalysis.averageCostPerBird) || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {getTrendIcon()}
            </div>
            <div className="ml-4">
              <p className="text-sm text-slate-500">Trend</p>
              <p className={`text-2xl font-semibold ${
                costAnalysis.trendDirection === 'up' ? 'text-red-600' :
                costAnalysis.trendDirection === 'down' ? 'text-green-600' : 'text-slate-600'
              }`}>
                {costAnalysis.trendPercentage > 0 ? '+' : ''}{(typeof costAnalysis.trendPercentage === 'number' ? costAnalysis.trendPercentage : parseFloat(costAnalysis.trendPercentage) || 0).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cost Trend Chart */}
        <div className="lg:col-span-2 bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Cost Trend</h3>
          <div className="h-64">
            <Line 
              data={costTrendChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top' as const,
                  },
                },
                                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Cost'
                      }
                    }
                  }
              }}
            />
          </div>
        </div>

        {/* Category Distribution */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Cost by Category</h3>
          <div className="h-64">
            <Doughnut 
              data={categoryDistributionData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom' as const,
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Top Expense Components */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-slate-900">Top Expense Components</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Component
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Total Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Percentage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Quantity Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Avg Cost/kg
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {costAnalysis.topExpenseComponents.map((component, index) => (
                <tr key={component.name} className="hover:bg-slate-50/80">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-3 ${
                        index === 0 ? 'bg-red-500' :
                        index === 1 ? 'bg-yellow-500' :
                        index === 2 ? 'bg-green-500' : 'bg-gray-400'
                      }`}></div>
                      <div className="text-sm font-medium text-slate-900">{component.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {formatCurrency(typeof component.cost === 'number' ? component.cost : parseFloat(component.cost) || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className="bg-cyan-600 h-2 rounded-full"
                          style={{ width: `${component.percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-slate-900">{(typeof component.percentage === 'number' ? component.percentage : parseFloat(component.percentage) || 0).toFixed(1)}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {(typeof component.quantity === 'number' ? component.quantity : parseFloat(component.quantity) || 0).toFixed(1)} kg
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {formatCurrency((typeof component.cost === 'number' ? component.cost : parseFloat(component.cost) || 0) / (typeof component.quantity === 'number' ? component.quantity : parseFloat(component.quantity) || 1))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cost Optimization Recommendations */}
      {costAnalysis.trendDirection === 'up' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <CalculatorIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Cost Optimization Recommendations</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <ul className="list-disc pl-5 space-y-1">
                  <li>Costs have increased by {(typeof costAnalysis.trendPercentage === 'number' ? costAnalysis.trendPercentage : parseFloat(costAnalysis.trendPercentage) || 0).toFixed(1)}% - consider reviewing supplier contracts</li>
                  <li>Top expense component ({costAnalysis.topExpenseComponents[0]?.name}) accounts for {(typeof costAnalysis.topExpenseComponents[0]?.percentage === 'number' ? costAnalysis.topExpenseComponents[0]?.percentage : parseFloat(costAnalysis.topExpenseComponents[0]?.percentage) || 0).toFixed(1)}% of costs</li>
                  <li>Projected next month cost: {formatCurrency(typeof costAnalysis.projectedCost === 'number' ? costAnalysis.projectedCost : parseFloat(costAnalysis.projectedCost) || 0)}</li>
                  <li>Consider bulk purchasing or finding alternative suppliers for high-cost components</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 